import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  TimeRange,
  TIME_RANGES,
} from "components/charts/TimeRangeNavigator";
import { format } from "date-fns";

const DATE_TIME_LABEL_FORMAT = "MMM dd, yyyy HH:mm";

export const useTimeNavigation = () => {
  // Define which time options we want to show in the selector
  const DISPLAYED_TIME_RANGES = [
    TIME_RANGES[0], // 1h
    TIME_RANGES[1], // 3h
    TIME_RANGES[3], // 24h
    TIME_RANGES[4], // 3d
    TIME_RANGES[5], // 7d
    TIME_RANGES[6], // 30d
    TIME_RANGES[8], // 1y
    TIME_RANGES[10], // ALL
  ];

  // State for time range selection - default to 7 days
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGES[5]); // 7d default

  // Track whether the user has manually navigated using the arrows
  const hasManuallyNavigated = useRef(false);

  // State for time navigation (how many periods back we are)
  const [navigationOffset, setNavigationOffset] = useState(0);

  // State for debounced navigation offset (the one used for API calls)
  const [debouncedNavigationOffset, setDebouncedNavigationOffset] = useState(0);

  // State for custom date range selection
  const [customDateRange, setCustomDateRange] = useState<{
    fromTimestamp: number;
    toTimestamp: number;
  } | null>(null);

  // Ref for debounce timer
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounce effect for navigation offset changes
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedNavigationOffset(navigationOffset);
    }, 200); // 200ms debounce for snappier navigation

    // Cleanup timer on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [navigationOffset]);

  // Calculate time range based on selection and navigation offset
  const calculateTimeRange = useCallback(
    (offset: number = debouncedNavigationOffset) => {
      // If we have a custom date range and timeRange is custom, use it
      if (customDateRange && timeRange.value === "custom") {
        return {
          fromTimestamp: customDateRange.fromTimestamp,
          toTimestamp: customDateRange.toTimestamp,
        };
      }

      const now = Date.now();
      let periodDuration: number;

      // For ALL, match backend max lookback (2 years)
      if (timeRange.value === "all") {
        periodDuration = 2 * 365 * 24 * 60 * 60 * 1000;
      }
      // For custom, check if it's a string or number
      else if (
        timeRange.value === "custom" ||
        typeof timeRange.value === "string"
      ) {
        // If we get here with 'custom' it's probably an error, return 30 days as default
        periodDuration = 30 * 24 * 60 * 60 * 1000; // 30 days
      }
      // For numeric intervals (in milliseconds)
      else if (typeof timeRange.value === "number") {
        periodDuration = timeRange.value;
      }
      // Fallback to 30 days (1 month)
      else {
        periodDuration = 30 * 24 * 60 * 60 * 1000;
      }

      // Calculate to and from considering navigation offset
      const to = now - offset * periodDuration;
      const from = to - periodDuration;

      const result = {
        fromTimestamp: from,
        toTimestamp: to,
      };

      return result;
    },
    [timeRange, debouncedNavigationOffset, customDateRange],
  );

  // Ensure React Query recognizes changes
  const { fromTimestamp, toTimestamp } = useMemo(() => {
    return calculateTimeRange();
  }, [calculateTimeRange]);

  // Time range change handler
  const handleTimeRangeChange = (newRange: TimeRange) => {
    // Calculate the center date of current period to maintain reference
    const currentTimeRange = calculateTimeRange(navigationOffset);
    const currentCenterDate =
      (currentTimeRange.fromTimestamp + currentTimeRange.toTimestamp) / 2;

    // Calculate new period duration
    let newPeriodDuration: number;

    if (newRange.value === "all") {
      newPeriodDuration = 2 * 365 * 24 * 60 * 60 * 1000; // 2 years (matches chart-logs max)
    } else if (
      newRange.value === "custom" ||
      typeof newRange.value === "string"
    ) {
      newPeriodDuration = 30 * 24 * 60 * 60 * 1000; // 30 days default
    } else if (typeof newRange.value === "number") {
      newPeriodDuration = newRange.value;
    } else {
      newPeriodDuration = 30 * 24 * 60 * 60 * 1000; // fallback
    }

    // Calculate new offset to maintain center date
    const now = Date.now();
    const desiredEndTime = currentCenterDate + newPeriodDuration / 2;
    const newOffset = Math.round((now - desiredEndTime) / newPeriodDuration);

    setTimeRange(newRange);

    if (hasManuallyNavigated.current) {
      // User has navigated: maintain center date
      const finalOffset = Math.max(0, newOffset);
      setNavigationOffset(finalOffset);
      setDebouncedNavigationOffset(finalOffset);
    } else {
      // User hasn't navigated: always go to now
      setNavigationOffset(0);
      setDebouncedNavigationOffset(0);
    }
  };

  // Time navigation functions
  const navigateBackward = () => {
    hasManuallyNavigated.current = true;
    if (customDateRange && timeRange.value === "custom") {
      // For custom ranges, calculate the duration and move backward by that duration
      const duration =
        customDateRange.toTimestamp - customDateRange.fromTimestamp;
      const newFromTimestamp = customDateRange.fromTimestamp - duration;
      const newToTimestamp = customDateRange.toTimestamp - duration;

      setCustomDateRange({
        fromTimestamp: newFromTimestamp,
        toTimestamp: newToTimestamp,
      });

      // Update the label to reflect new dates
      const newFromDate = new Date(newFromTimestamp);
      const newToDate = new Date(newToTimestamp);
      setTimeRange({
        label: `${format(newFromDate, "MMM dd")} - ${format(
          newToDate,
          "MMM dd",
        )}`,
        value: "custom",
        type: "custom",
      });
    } else {
      setNavigationOffset((prev) => prev + 1);
    }
  };

  const navigateForward = () => {
    hasManuallyNavigated.current = true;
    if (customDateRange && timeRange.value === "custom") {
      // For custom ranges, calculate the duration and move forward by that duration
      const duration =
        customDateRange.toTimestamp - customDateRange.fromTimestamp;
      const newFromTimestamp = customDateRange.fromTimestamp + duration;
      const newToTimestamp = customDateRange.toTimestamp + duration;

      // Don't allow navigation beyond current time
      if (newToTimestamp <= Date.now()) {
        setCustomDateRange({
          fromTimestamp: newFromTimestamp,
          toTimestamp: newToTimestamp,
        });

        // Update the label to reflect new dates
        const newFromDate = new Date(newFromTimestamp);
        const newToDate = new Date(newToTimestamp);
        setTimeRange({
          label: `${format(newFromDate, "MMM dd")} - ${format(
            newToDate,
            "MMM dd",
          )}`,
          value: "custom",
          type: "custom",
        });
      }
    } else {
      setNavigationOffset((prev) => Math.max(0, prev - 1));
    }
  };

  // Function to return to current time
  const returnToNow = () => {
    hasManuallyNavigated.current = false;
    setNavigationOffset(0);
    // Clear custom date range when returning to now
    if (timeRange.value === "custom") {
      setCustomDateRange(null);
      setTimeRange(TIME_RANGES[5]); // Return to 7d default
    }
  };

  // Function to handle custom date range selection
  const handleCustomDateRange = (fromDate: Date, toDate: Date) => {
    const now = Date.now();
    const cappedTo = Math.min(toDate.getTime(), now);
    const cappedFrom = Math.min(fromDate.getTime(), cappedTo - 1);

    const customRange: TimeRange = {
      label: `${format(new Date(cappedFrom), "MMM dd")} - ${format(
        new Date(cappedTo),
        "MMM dd",
      )}`,
      value: "custom",
      type: "custom",
    };

    setCustomDateRange({
      fromTimestamp: cappedFrom,
      toTimestamp: cappedTo,
    });
    setTimeRange(customRange);
    setNavigationOffset(0);
    setDebouncedNavigationOffset(0);
  };

  // Function to format current period based on offset
  const getCurrentPeriodLabel = () => {
    if (timeRange.value === "all") return "Last 2 Years";
    const { fromTimestamp, toTimestamp } = calculateTimeRange(navigationOffset);
    const fromDate = new Date(fromTimestamp);
    const toDate = new Date(toTimestamp);

    if (navigationOffset === 0) {
      return `Current (${format(fromDate, DATE_TIME_LABEL_FORMAT)} - ${format(
        toDate,
        DATE_TIME_LABEL_FORMAT,
      )})`;
    }

    return `${format(fromDate, DATE_TIME_LABEL_FORMAT)} - ${format(
      toDate,
      DATE_TIME_LABEL_FORMAT,
    )}`;
  };

  // Function to check if we're at current time for navigation buttons
  const isAtCurrentTime = () => {
    if (customDateRange && timeRange.value === "custom") {
      // For custom ranges, check if the end time is within 5 minutes of current time
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      return Math.abs(customDateRange.toTimestamp - now) < fiveMinutes;
    }
    return navigationOffset === 0;
  };

  // Get current dates for date picker synchronization
  const getCurrentDates = () => {
    const { fromTimestamp, toTimestamp } = calculateTimeRange(navigationOffset);
    return {
      fromDate: new Date(fromTimestamp),
      toDate: new Date(toTimestamp),
    };
  };

  return {
    timeRange,
    navigationOffset,
    fromTimestamp,
    toTimestamp,
    displayedTimeRanges: DISPLAYED_TIME_RANGES,
    periodLabel: getCurrentPeriodLabel(),
    handleTimeRangeChange,
    navigateBackward,
    navigateForward,
    returnToNow,
    handleCustomDateRange,
    getCurrentDates,
    isAtCurrentTime,
  };
};
